<template>
	<div ref="el" data-component="features">
		<div v-if="!isMobile" class="background">
			<VideoAsset :asset="backgroundVideoDesktop" :autoplay="true" />
		</div>

		<div class="content-wrapper">
			<div v-if="isMobile" class="mobile-images">
				<div class="video-container">
					<VideoAsset :asset="backgroundVideoMobile" :autoplay="true" />
				</div>
				<div class="image-container">
					<ImageAsset :asset="mobilePatch" />
				</div>
			</div>

			<div class="g-row list-container">
				<div class="features-list g-col xxl-offset-11 xs-offset-0">
					<FeaturePoint v-for="feature in featuresList" :key="feature._key" :description="feature.description" />
				</div>
			</div>

			<!-- <div class="bottom-note g-row -text-3">
				<div class="circle-mask"></div>
				<div class="g-col xxl-3 md-5 xs-12 bottom-text">
					<SanityContent :blocks="bottomNote" />
				</div>
			</div> -->
		</div>
	</div>
</template>

<script setup>
import { gsap, ScrollTrigger } from 'gsap/all'

import { FeaturePoint } from '~/components/primitives'
import { ImageAsset, VideoAsset } from '~/components/core'

import { useScreen } from 'vue-screen'
import { breakpoints } from '~/config'

import.meta.client && gsap.registerPlugin(ScrollTrigger)

const props = defineProps({
	qualitiesList: { type: Array, default: undefined },
	featuresList: { type: Array, default: undefined },
	bottomNote: { type: Array, default: undefined },

	backgroundVideoDesktop: { type: Object, default: undefined },
	backgroundVideoMobile: { type: Object, default: undefined },
	mobilePatch: { type: Object, default: undefined },
})

const el = ref(null)
const context = inject('pageContext')

const screen = useScreen()
const isMobile = ref(null)

watch(
	() => screen?.width,
	(width) => {
		isMobile.value = width < breakpoints.xsmall
	}
)

let scrollTrigger = null

const setup = async () => {
	isMobile.value = screen.width < breakpoints.xsmall

	await nextTick()

	const featurePoints = el.value.querySelectorAll('[data-component="feature-point"]')

	gsap.set(featurePoints, {
		opacity: 0,
		x: -5,
	})

	gsap.set(el.value.querySelectorAll('svg'), {
		rotation: -360,
	})

	scrollTrigger = ScrollTrigger.create({
		trigger: featurePoints[0],
		start: 'top center',
		onEnter: () => {
			gsap.to(featurePoints, {
				opacity: 1,
				x: 0,
				duration: 0.7,
				stagger: 0.1,
				ease: 'expo.out',
			})

			gsap.to(el.value.querySelectorAll('svg'), {
				rotation: 0,
				duration: 3.0,
				stagger: 0.1,
				ease: 'expo.out',
			})
		},
	})
}

const init = () => {}

const kill = () => {
	scrollTrigger?.kill()
}

onBeforeUnmount(() => {
	kill()
})

onMounted(() => {
	context.$page.loader.ready.then(() => setup())
})
</script>

<style lang="scss" scoped>
[data-component='features'] {
	position: relative;

	width: 100%;

	height: fit-content;
	overflow: hidden;

	background-color: var(--color-geral-creme);

	@include max-width($xs) {
		padding-bottom: rs(80, 80);
	}

	.background {
		position: relative;
		width: 100%;
		height: auto;
		display: block;

		:deep(video) {
			width: 100%;
			height: auto;
			object-fit: contain;
			display: block;
		}
	}

	.content-wrapper {
		position: absolute;
		inset: 0;

		@include max-width($xs) {
			position: relative;
			padding: 0;
		}
	}

	.mobile-images {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		z-index: 0;

		display: flex;
		flex-direction: column;

		.video-container {
			position: relative;
			height: auto;
			width: 100%;
			flex: 0 0 auto;

			z-index: -1;

			:deep(video) {
				width: 100%;
				height: auto;
				object-fit: contain;
				object-position: center;
				display: block;
			}
		}

		.image-container {
			position: relative;
			height: auto;
			width: 120%;
			flex: 0 0 auto;

			top: -42px;
			left: 50%;

			z-index: 0;

			mix-blend-mode: luminosity;
			filter: blur(18px);
			transform: translateX(-50%);

			:deep(img) {
				width: 100%;
				height: auto;
				object-fit: cover;
				object-position: top;
				transform: rotate(-1deg);
			}
		}
	}

	.list-container {
		height: 100%;
	}

	.features-list {
		height: 100%;

		display: flex;
		flex-direction: column;
		justify-content: space-between;

		padding: 8vw 0;

		@include max-width($lg) {
			padding: 5vw 0;
		}

		@include max-width($xs) {
			padding-top: 150vw;
			min-height: 180vw;
			padding-bottom: 0;
			gap: rs(64);
		}

		:deep([data-component='feature-point']) {
			&:nth-child(2) {
				margin-left: rs(100);
			}
			&:nth-child(3) {
				margin-left: rs(100);
			}
			&:nth-child(5) {
				margin-left: calc(-1 * #{rs(150)});
			}

			@include max-width($xs) {
				&:nth-child(2),
				&:nth-child(3),
				&:nth-child(5) {
					margin-left: 0;
				}
			}
		}
	}

	.bottom-note {
		position: absolute;
		bottom: var(--grid-margin);
		color: var(--color-geral-brown);

		@include max-width($xs) {
			position: relative;
			bottom: 0;
			margin-left: rs(28, 24);
			padding-top: rs(64, 64);
		}

		.bottom-text {
			z-index: 1;
		}

		.circle-mask {
			position: absolute;
			left: rs(-100);
			bottom: rs(-150);

			width: 40vw;
			height: 40vw;
			z-index: 0;

			border-radius: 100%;
			background: rgb(from var(--color-geral-creme) r g b / 0.3);

			filter: blur(50px);

			@include max-width($xs) {
				display: none;
			}
		}

		:deep(strong) {
			opacity: 0.4;
		}

		:deep(p:not(:last-child)) {
			margin-bottom: rs(12, 12);
		}
	}
}
</style>
